import { zodResolver } from "@hookform/resolvers/zod"
import { Alert, AlertDescription, AlertTitle } from "design-system/atoms/Alert"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useTrackEvent } from "lib/mixpanel"
import { TRACKING_EVENTS } from "lopermut"
import { CircleCheck } from "lucide-react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateReferralLeadSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { useCreateReferralMutation } from "../hooks/useCreateReferralMutation"
import { useGetReferralLeadsQuery } from "../hooks/useGetReferralLeadsQuery"

type ReferralLeadFormValues = z.infer<typeof CreateReferralLeadSchema>

const getWebsiteHref = (website: string) => {
  if (website.startsWith("http://") || website.startsWith("https://")) {
    return website
  }

  return `https://${website}`
}

export function ReferralsPage() {
  const { t } = useTranslation()
  const trackEvent = useTrackEvent()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const referralCredits = {
    recommendedCompany: "500",
    registeredCompany: "1000",
    clientConversion: "3000",
  }
  const {
    data: referralLeads,
    isPending: isReferralLeadsPending,
    refetch,
  } = useGetReferralLeadsQuery()

  const form = useForm<ReferralLeadFormValues>({
    resolver: zodResolver(CreateReferralLeadSchema),
    defaultValues: {
      companyName: "",
      website: "",
      contactName: "",
      role: "",
      email: "",
      phone: "",
    },
    mode: "onChange",
  })

  const { mutateAsync, isPending } = useCreateReferralMutation()

  const onSubmit = async (data: ReferralLeadFormValues) => {
    await mutateAsync(data)
    toast.success(t("subscription.referrals.success"))
    form.reset()
    await refetch()
    setIsFormOpen(false)
  }

  const renderWebsiteCell = (website: string | null | undefined) => {
    if (!website) {
      return "-"
    }

    return (
      <a
        href={getWebsiteHref(website)}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline"
      >
        {website}
      </a>
    )
  }

  const openReferralForm = () => {
    trackEvent(TRACKING_EVENTS.REFERRALS_REFER_COMPANY_CLICKED)
    setIsFormOpen(true)
  }

  const hasReferralLeads = (referralLeads ?? []).length > 0
  const shouldShowWelcome = !isReferralLeadsPending && !hasReferralLeads

  const renderTableBodyContent = () => {
    if (isReferralLeadsPending) {
      return <TableSkeleton colSpans={[1, 1, 1, 1, 1, 1, 1, 1]} />
    }

    if ((referralLeads ?? []).length === 0) {
      return <TableEmptySlate colSpan={8} message={t("subscription.referrals.list.table.empty")} />
    }

    return (referralLeads ?? []).map((lead) => (
      <TableRow key={lead.id}>
        <TableCell>
          <DateDisplay date={lead.createdAt} />
        </TableCell>
        <TableCell>{lead.companyName}</TableCell>
        <TableCell>{renderWebsiteCell(lead.website)}</TableCell>
        <TableCell>{lead.contactName}</TableCell>
        <TableCell>{lead.role || "-"}</TableCell>
        <TableCell>{lead.email}</TableCell>
        <TableCell>{lead.phone}</TableCell>
        <TableCell>{lead.credits}</TableCell>
      </TableRow>
    ))
  }

  return (
    <div className="flex flex-col gap-6">
      {shouldShowWelcome ? (
        <div className="flex flex-col items-center text-center gap-6 px-4 py-6">
          <div className="max-w-3xl mb-10">
            <h1 className="text-4xl font-medium md:text-5xl mb-10">
              {t("subscription.referrals.welcome.title")}
            </h1>
            <p className="text-muted-foreground mt-3 text-base md:text-lg">
              {t("subscription.referrals.welcome.description")}
            </p>
          </div>

          <Alert
            variant="default"
            className="max-w-4xl border-foreground border-2 p-6 text-left md:p-8"
          >
            <AlertTitle className="text-xl text-center md:text-2xl mb-6">
              {t("subscription.referrals.welcome.card.title")}
            </AlertTitle>

            <div className="mt-4 flex flex-col gap-3">
              <AlertDescription className="flex items-start gap-3 text-base">
                <CircleCheck className="text-success mt-0.5 h-5 w-5 shrink-0" />
                {t("subscription.referrals.welcome.card.steps.recommendedCompany", {
                  credits: referralCredits.recommendedCompany,
                })}
              </AlertDescription>
              <AlertDescription className="flex items-start gap-3 text-base">
                <CircleCheck className="text-success mt-0.5 h-5 w-5 shrink-0" />
                {t("subscription.referrals.welcome.card.steps.registeredCompany", {
                  credits: referralCredits.registeredCompany,
                })}
              </AlertDescription>
              <AlertDescription className="flex items-start gap-3 text-base">
                <CircleCheck className="text-success mt-0.5 h-5 w-5 shrink-0" />
                {t("subscription.referrals.welcome.card.steps.clientConversion", {
                  credits: referralCredits.clientConversion,
                })}
              </AlertDescription>
            </div>

            <AlertDescription className="text-muted-foreground mt-5 border-t pt-4 text-base">
              {t("subscription.referrals.welcome.card.multiplier")}
            </AlertDescription>
          </Alert>

          <Button onClick={openReferralForm} variant="default" className="w-full sm:w-auto">
            {t("subscription.referrals.list.recommend")}
          </Button>
        </div>
      ) : (
        <>
          <div>
            <h1 className="text-2xl font-semibold">{t("subscription.referrals.list.title")}</h1>
            <p className="text-muted-foreground mt-1">
              {t("subscription.referrals.list.description")}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex justify-end">
              <Button onClick={openReferralForm} variant="default" className="w-full sm:w-auto">
                {t("subscription.referrals.list.recommend")}
              </Button>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("subscription.referrals.list.table.createdAt")}</TableHead>
                  <TableHead>{t("subscription.referrals.list.table.companyName")}</TableHead>
                  <TableHead>{t("subscription.referrals.list.table.website")}</TableHead>
                  <TableHead>{t("subscription.referrals.list.table.contactName")}</TableHead>
                  <TableHead>{t("subscription.referrals.list.table.role")}</TableHead>
                  <TableHead>{t("subscription.referrals.list.table.email")}</TableHead>
                  <TableHead>{t("subscription.referrals.list.table.phone")}</TableHead>
                  <TableHead>{t("subscription.referrals.list.table.credits")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderTableBodyContent()}</TableBody>
            </Table>
          </div>
        </>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("subscription.referrals.form.title")}</DialogTitle>
            <DialogDescription>{t("subscription.referrals.form.description")}</DialogDescription>
          </DialogHeader>

          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("subscription.referrals.fields.companyName")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("subscription.referrals.fields.contactName")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("subscription.referrals.fields.email")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("subscription.referrals.fields.role")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="text" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("subscription.referrals.fields.phone")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="tel" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("subscription.referrals.fields.website")}</FormLabel>
                  <FormControl>
                    <Input {...field} type="text" placeholder="example.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsFormOpen(false)}
                disabled={isPending}
              >
                {t("subscription.referrals.form.back")}
              </Button>
              <Button
                onClick={() => trackEvent(TRACKING_EVENTS.REFERRALS_SEND_REFERRAL_CLICKED)}
                disabled={isPending || !form.formState.isValid}
              >
                {t("subscription.referrals.form.submit")}
              </Button>
            </div>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
